import { useEffect, useState } from 'react';

const GlobalErrorHandler = () => {
  const [error, setError] = useState(null);
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);

  useEffect(() => {
    // Detect in-app browser
    const detectInAppBrowser = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // Instagram patterns
      const isInstagram = /Instagram/.test(userAgent) || 
                         /FBAN|FBAV/.test(userAgent) || 
                         /FB_IAB/.test(userAgent) ||
                         window.location.search.includes('ig_web_button_share_sheet');
      
      // Other social media in-app browsers
      const isOtherInApp = /TikTok|TwitterAndroid|LinkedInApp|WhatsApp|Telegram/.test(userAgent);
      
      return isInstagram || isOtherInApp;
    };

    setIsInAppBrowser(detectInAppBrowser());
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      console.error('🚨 Unhandled promise rejection:', event.reason);
      
      // Show user-friendly message for specific Firebase errors
      if (event.reason?.code === 'app-check/fetch-status-error' || 
          event.reason?.message?.includes('reCAPTCHA') ||
          event.reason?.message?.includes('addEventListener')) {
        setError({
          title: 'Connection Issue',
          message: 'Having trouble connecting to our services. This might be due to network restrictions when accessing via IP address.',
          suggestion: 'Try accessing via localhost instead, or check your internet connection.'
        });
      } else if (event.reason?.code?.startsWith('auth/')) {
        setError({
          title: 'Authentication Issue',
          message: 'There was a problem with authentication.',
          suggestion: 'Please try refreshing the page or logging in again.'
        });
      } else {
        setError({
          title: 'Something went wrong',
          message: 'We encountered an unexpected error.',
          suggestion: 'Please try refreshing the page. If the problem persists, try again later.'
        });
      }
      
      // Prevent the default browser error handling
      event.preventDefault();
    };

    // Handle regular JavaScript errors
    const handleError = (event) => {
      console.error('🚨 Global JavaScript error:', event.error);
      
      setError({
        title: 'Application Error',
        message: 'Something unexpected happened.',
        suggestion: 'Please refresh the page to continue.'
      });
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  const handleOpenInBrowser = () => {
    const currentUrl = window.location.href;
    
    // For Android devices, try intent scheme
    if (/Android/.test(navigator.userAgent)) {
      const intentUrl = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;end`;
      window.location.href = intentUrl;
    }
    // For iOS devices, copy URL and show instructions
    else if (/iPhone|iPad/.test(navigator.userAgent)) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(currentUrl).then(() => {
          alert('URL copied to clipboard! Please paste it in Safari or your preferred browser.');
        });
      } else {
        alert('Please copy this URL and open it in Safari: ' + currentUrl);
      }
    }
    // For other platforms, try opening new window
    else {
      const newWindow = window.open(currentUrl, '_blank');
      if (!newWindow) {
        alert('Please manually open this URL in your browser: ' + currentUrl);
      }
    }
  };

  if (!error) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{error.title}</h3>
        </div>
        
        <p className="text-gray-600 mb-2">{error.message}</p>
        <p className="text-sm text-gray-500 mb-6">
          {isInAppBrowser 
            ? 'For the best experience, please open this app in your device\'s browser instead.'
            : error.suggestion
          }
        </p>
        
        <div className="flex gap-3">
          {isInAppBrowser ? (
            <button
              onClick={handleOpenInBrowser}
              className="flex-1 bg-bela-600 hover:bg-bela-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Open in Browser
            </button>
          ) : (
            <button
              onClick={() => window.location.reload()}
              className="flex-1 bg-bela-600 hover:bg-bela-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Refresh Page
            </button>
          )}
          <button
            onClick={() => setError(null)}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalErrorHandler;
